import { useEffect, useState } from 'react';
import {
  BODY_TYPE_LABEL,
  FLEET_STATE_LABEL,
  ORDER_STATUS_LABEL,
  TOWED_KINDS,
  VEHICLE_KIND_LABEL,
  formatRub,
  vehicleTitle,
  type CrewRow,
  type FileRow,
  type FleetRow,
  type VehicleTripRow,
} from 'shared';
import { api } from '../api';
import CountryFlag from '../CountryFlag';
import Icon from '../Icon';
import Modal from '../Modal';
import { FileGrid, UploadButton } from '../Files';
import FleetAdd from './FleetAdd';
import { confirm, haptic } from '../telegram';
import { toast } from '../toast';

interface Props {
  companyId: string;
  /** Правит парк только тот, кто им распоряжается: владелец и логист. */
  canManage: boolean;
}

/**
 * Парк компании: машины и прицепы.
 *
 * Отдельно от экипажа — это разные сущности. Раньше техника перечислялась
 * строкой в карточке водителя, и у конторы на десять машин выходила
 * нечитаемая простыня, из которой нельзя было понять, какая машина стоит
 * свободной. Вопрос «кто свободен» и вопрос «что свободно» — разные,
 * и списка тоже должно быть два.
 *
 * Прицеп здесь не «машина без водителя», а своя единица: у него нет рейсов
 * и телефона, он цепляется к тягачу и переходит от одной машины к другой.
 */
export default function Fleet({ companyId, canManage }: Props) {
  const [items, setItems] = useState<FleetRow[]>([]);
  const [crew, setCrew] = useState<CrewRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState<FleetRow | null>(null);

  async function load() {
    try {
      const [f, c] = await Promise.all([api.fleet(companyId), api.crew(companyId)]);
      setItems(f.items);
      /*
       * Кого можно поставить за руль.
       *
       * Не все сотрудники: у диспетчера без галочки «прикреплять к авто»
       * в этом списке делать нечего — иначе в подборе экипажа половина
       * конторы, которая машину в глаза не видела.
       */
      setCrew(c.items.filter((m) => m.active && m.canDrive));
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  async function patch(
    id: string,
    data: Parameters<typeof api.patchVehicle>[1],
    done: string,
  ) {
    try {
      await api.patchVehicle(id, data);
      await load();
      toast.success(done);
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  async function retire(v: FleetRow) {
    if (
      !(await confirm(
        `Вывести ${v.plate} из парка? Историю рейсов это не тронет.`,
      ))
    ) {
      return;
    }
    await patch(v.id, { active: false }, `${v.plate} выведена из парка`);
  }

  /**
   * Удаление.
   *
   * Сервер сам решает, можно ли стереть запись: техника, которая уже возила,
   * выводится из парка, потому что на неё ссылаются рейсы и документы.
   * Здесь мы только честно говорим, чем всё кончилось.
   */
  async function remove(v: FleetRow) {
    if (!(await confirm(`Удалить ${v.plate} из парка?`))) return;

    try {
      const res = await api.deleteVehicle(v.id);
      await load();
      toast.success(
        res.kept
          ? `${v.plate} возила грузы — выведена из парка, история сохранена`
          : `${v.plate} удалена`,
      );
    } catch (e) {
      toast.error((e as Error).message);
    }
  }

  if (loading) return <div className="card">Загружаю…</div>;
  if (error) return <div className="empty">{error}</div>;

  const trucks = items.filter((v) => !TOWED_KINDS.includes(v.kind));
  const towed = items.filter((v) => TOWED_KINDS.includes(v.kind));

  return (
    <>
      {canManage && <FleetAdd companyId={companyId} onDone={() => void load()} />}

      {items.length === 0 && (
        <div className="empty">
          В парке пока пусто. Заведите машину или прицеп — они запишутся
          на компанию, а не на человека.
        </div>
      )}

      {trucks.length > 0 && (
        <>
          <h3>
            Машины ({trucks.filter((v) => v.state === 'free').length} свободно из{' '}
            {trucks.length})
          </h3>
          <div className="feed-grid">
            {trucks.map((v) => (
              <FleetCard
                key={v.id}
                row={v}
                crew={crew}
                canManage={canManage}
                onPatch={(data, done) => void patch(v.id, data, done)}
                onRetire={() => void retire(v)}
                onReturn={() => void patch(v.id, { active: true }, `${v.plate} снова в парке`)}
                onDelete={() => void remove(v)}
                onOpen={() => setOpen(v)}
              />
            ))}
          </div>
        </>
      )}

      {towed.length > 0 && (
        <>
          <h3>Прицепы и платформы ({towed.length})</h3>
          <div className="feed-grid">
            {towed.map((v) => (
              <FleetCard
                key={v.id}
                row={v}
                crew={crew}
                canManage={canManage}
                trucks={trucks}
                onPatch={(data, done) => void patch(v.id, data, done)}
                onRetire={() => void retire(v)}
                onReturn={() => void patch(v.id, { active: true }, `${v.plate} снова в парке`)}
                onDelete={() => void remove(v)}
                onOpen={() => setOpen(v)}
              />
            ))}
          </div>
        </>
      )}

      <Modal
        open={open !== null}
        onClose={() => setOpen(null)}
        title={open ? vehicleTitle(open) : undefined}
      >
        {open && (
          <VehicleDetail
            vehicle={open}
            canManage={canManage}
            onSaved={async () => {
              await load();
              setOpen(null);
            }}
          />
        )}
      </Modal>
    </>
  );
}

interface CardProps {
  row: FleetRow;
  crew: CrewRow[];
  canManage: boolean;
  /** Тягачи для сцепки. Передаётся только карточке прицепа. */
  trucks?: FleetRow[];
  onPatch: (data: Parameters<typeof api.patchVehicle>[1], done: string) => void;
  onRetire: () => void;
  onReturn: () => void;
  onDelete: () => void;
  onOpen: () => void;
}

function FleetCard({
  row,
  crew,
  canManage,
  trucks,
  onPatch,
  onRetire,
  onReturn,
  onDelete,
  onOpen,
}: CardProps) {
  const towed = TOWED_KINDS.includes(row.kind);
  const paired = row.crewSize > 1;

  /** Закрепление одного места экипажа. Список отправляется целиком. */
  function setSeat(index: number, userId: string) {
    const next = [...row.crew.map((c) => c.id)];
    if (userId) next[index] = userId;
    else next.splice(index, 1);

    // Один человек дважды за одной машиной — не пара, а опечатка.
    const unique = [...new Set(next.filter(Boolean))];
    onPatch({ crew: unique }, 'Экипаж обновлён');
  }

  return (
    <div className={`card fleet fleet--${row.state}`}>
      <div className="crew__head">
        <strong className="tabular">{row.plate}</strong>
        <span className="badge">{VEHICLE_KIND_LABEL[row.kind]}</span>
        <span
          className={`badge ${
            row.state === 'free'
              ? 'badge--direct'
              : row.state === 'busy'
                ? 'badge--bad'
                : ''
          }`}
        >
          {FLEET_STATE_LABEL[row.state]}
        </span>
      </div>

      {/* Паспорт машины: марка, модель, год, цвет. По ним технику узнают
          на площадке, а не по типу кузова. */}
      {(row.make || row.color || row.year) && (
        <div className="meta">
          {[row.color, row.make, row.model].filter(Boolean).join(' ')}
          {row.year ? ` · ${row.year} г.` : ''}
        </div>
      )}

      <div className="meta">
        {BODY_TYPE_LABEL[row.bodyType]} · {(row.capacityKg / 1000).toFixed(1)} т
        {row.volumeM3 ? ` · ${row.volumeM3} м³` : ''}
        {row.adr ? ' · ADR' : ''}
      </div>

      {row.trip && (
        <div className="route" style={{ marginTop: 8 }}>
          <CountryFlag address={row.trip.fromAddress} />
          {row.trip.fromAddress}
          <Icon name="arrow" size={15} />
          <CountryFlag address={row.trip.toAddress} />
          {row.trip.toAddress}
          <span className="meta"> · {ORDER_STATUS_LABEL[row.trip.status]}</span>
        </div>
      )}

      {/* Сводка карточки: кто закреплён и что прицеплено. Ради этих двух
          строк экран и открывают. */}
      {!towed && (
        <div className="meta" style={{ marginTop: 8 }}>
          {row.crew.length > 0
            ? `Закреплены: ${row.crew.map((c) => c.name).join(', ')}`
            : 'Водитель не закреплён'}
          {row.hitched.length > 0
            ? ` · прицеп ${row.hitched.map((h) => h.plate).join(', ')}`
            : ' · без прицепа'}
        </div>
      )}

      {canManage && !towed && (
        <>
          <label className="check" style={{ marginTop: 8 }}>
            <input
              type="checkbox"
              checked={paired}
              onChange={(e) =>
                onPatch(
                  // Снимая пару, обрезаем и список: иначе второй водитель
                  // остаётся закреплённым за машиной, которая уже не парная.
                  e.target.checked
                    ? { crewSize: 2 }
                    : { crewSize: 1, crew: row.crew.slice(0, 1).map((c) => c.id) },
                  e.target.checked ? 'Машина стала парной' : 'Экипаж — один водитель',
                )
              }
            />
            работают в паре
          </label>

          {paired && (
            <div className="field field--tight">
              <label>Человек в экипаже</label>
              <input
                type="number"
                min={1}
                max={2}
                value={row.crewSize}
                onChange={(e) =>
                  onPatch(
                    { crewSize: Math.min(2, Math.max(1, Number(e.target.value) || 1)) },
                    'Размер экипажа сохранён',
                  )
                }
              />
            </div>
          )}

          {Array.from({ length: row.crewSize }).map((_, i) => (
            <div key={i} className="field field--tight">
              <label>{row.crewSize > 1 ? `Водитель ${i + 1}` : 'Закреплён'}</label>
              <select
                value={row.crew[i]?.id ?? ''}
                onChange={(e) => setSeat(i, e.target.value)}
              >
                <option value="">Никто</option>
                {crew.map((m) => (
                  <option key={m.userId} value={m.userId}>
                    {m.name}
                  </option>
                ))}
              </select>
            </div>
          ))}
        </>
      )}

      {canManage && towed && (
        <div className="field field--tight">
          <label>Прицеплен к</label>
          <select
            value={row.hitchedTo?.id ?? ''}
            onChange={(e) =>
              onPatch(
                { hitchedToId: e.target.value || null },
                e.target.value ? 'Прицеп закреплён' : 'Прицеп отцеплен',
              )
            }
          >
            <option value="">Отцеплен</option>
            {(trucks ?? []).map((t) => (
              <option key={t.id} value={t.id}>
                {t.plate}
              </option>
            ))}
          </select>
        </div>
      )}

      {towed && !canManage && (
        <div className="meta" style={{ marginTop: 8 }}>
          {row.hitchedTo ? `Прицеплен к ${row.hitchedTo.plate}` : 'Отцеплен'}
        </div>
      )}

      {/* Итог по машине прямо в карточке: разговор «эта машина съедает
          больше, чем привозит» начинается именно с этих двух цифр. */}
      {row.money.ordersDone > 0 && (
        <div className="row" style={{ marginTop: 8 }}>
          <div>
            <div className="meta">Привезла</div>
            <strong>{formatRub(row.money.payoutRub)}</strong>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div className="meta">Расходы</div>
            <strong>{formatRub(row.money.expensesRub)}</strong>
          </div>
        </div>
      )}

      <div className="row" style={{ gap: 8, marginTop: 8 }}>
        <button
          className="secondary btn--compact"
          onClick={() => {
            haptic();
            onOpen();
          }}
        >
          История и документы
        </button>
        {canManage &&
          (row.active ? (
            <button className="secondary btn--compact" onClick={onRetire}>
              Вывести
            </button>
          ) : (
            // Выведенную технику возвращают: машину чинят и она снова ездит,
            // а «вывел по ошибке» иначе исправляется только новой карточкой
            // с потерей истории.
            <button className="secondary btn--compact" onClick={onReturn}>
              Вернуть в парк
            </button>
          ))}
        {canManage && <button className="danger btn--compact" onClick={onDelete}>Удалить</button>}
      </div>
    </div>
  );
}

/**
 * Что видно по нажатию: история рейсов с деньгами по каждому и документы.
 *
 * История нужна не ради архива, а ради разговора о деньгах: вывод «машина
 * за месяц съела больше, чем привезла» делают, глядя на список рейсов рядом
 * с расходами по каждому.
 */
function VehicleDetail({
  vehicle,
  canManage,
  onSaved,
}: {
  vehicle: FleetRow;
  canManage: boolean;
  onSaved: () => Promise<void>;
}) {
  const [trips, setTrips] = useState<VehicleTripRow[]>([]);
  const [files, setFiles] = useState<FileRow[]>([]);
  const [loading, setLoading] = useState(true);

  /** Правка паспортных данных единицы. Закрыта, пока её не открыли. */
  const [editing, setEditing] = useState(false);
  const [plate, setPlate] = useState(vehicle.plate);
  const [capacityT, setCapacityT] = useState(
    (vehicle.capacityKg / 1000).toString(),
  );
  const [volumeM3, setVolumeM3] = useState(vehicle.volumeM3?.toString() ?? '');
  const [adr, setAdr] = useState(vehicle.adr);
  const [heightM, setHeightM] = useState(vehicle.heightM?.toString() ?? '');
  const [widthM, setWidthM] = useState(vehicle.widthM?.toString() ?? '');
  const [lengthM, setLengthM] = useState(vehicle.lengthM?.toString() ?? '');
  const [grossT, setGrossT] = useState(
    vehicle.grossWeightKg ? (vehicle.grossWeightKg / 1000).toString() : '',
  );
  const [busy, setBusy] = useState(false);

  async function save() {
    if (plate.trim().length < 4) {
      toast.error('Укажите госномер');
      return;
    }
    if (!Number(capacityT)) {
      toast.error('Укажите грузоподъёмность');
      return;
    }

    setBusy(true);
    try {
      await api.patchVehicle(vehicle.id, {
        plate: plate.trim().toUpperCase(),
        capacityKg: Math.round(Number(capacityT) * 1000),
        // `null` — стереть значение, а не «не трогать»: пустое поле здесь
        // значит «неизвестно», и оно должно доезжать до базы.
        volumeM3: volumeM3 ? Number(volumeM3) : null,
        adr,
        heightM: heightM ? Number(heightM) : null,
        widthM: widthM ? Number(widthM) : null,
        lengthM: lengthM ? Number(lengthM) : null,
        grossWeightKg: grossT ? Math.round(Number(grossT) * 1000) : null,
      });
      toast.success('Изменения сохранены');
      await onSaved();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    Promise.all([api.vehicleTrips(vehicle.id), api.vehicleFiles(vehicle.id)])
      .then(([t, f]) => {
        setTrips(t.items);
        setFiles(f.items);
      })
      .catch((e: Error) => toast.error(e.message))
      .finally(() => setLoading(false));
  }, [vehicle.id]);

  const net = vehicle.money.payoutRub - vehicle.money.expensesRub;

  return (
    <>
      <div className="eco">
        <div>
          <span className="meta">Рейсов</span>
          <strong>{vehicle.money.ordersDone}</strong>
        </div>
        <div>
          <span className="meta">Привезла</span>
          <strong>{formatRub(vehicle.money.payoutRub)}</strong>
        </div>
        <div>
          <span className="meta">Чистыми</span>
          <strong className={net < 0 ? 'neg' : 'pos'}>{formatRub(net)}</strong>
        </div>
      </div>

      <div className="meta" style={{ marginTop: 8 }}>
        {BODY_TYPE_LABEL[vehicle.bodyType]} ·{' '}
        {(vehicle.capacityKg / 1000).toFixed(1)} т
        {vehicle.crew.length > 0
          ? ` · ${vehicle.crew.map((c) => c.name).join(', ')}`
          : ''}
      </div>

      {/* Правка спрятана за кнопкой: открывают карточку ради истории
          и документов, а номер с грузоподъёмностью меняют раз в жизни. */}
      {canManage && !editing && (
        <button
          className="secondary btn--compact"
          style={{ marginTop: 8 }}
          onClick={() => setEditing(true)}
        >
          Изменить данные
        </button>
      )}

      {canManage && editing && (
        <div style={{ marginTop: 12 }}>
          <div className="field-row">
            <div className="field">
              <label>Госномер</label>
              <input
                autoCapitalize="characters"
                value={plate}
                onChange={(e) => setPlate(e.target.value.toUpperCase())}
              />
            </div>
            <div className="field">
              <label>Грузоподъёмность, т</label>
              <input
                type="number"
                inputMode="decimal"
                value={capacityT}
                onChange={(e) => setCapacityT(e.target.value)}
              />
            </div>
          </div>

          <div className="field">
            <label>Объём, м³</label>
            <input
              type="number"
              inputMode="decimal"
              value={volumeM3}
              onChange={(e) => setVolumeM3(e.target.value)}
            />
          </div>

          <label className="check">
            <input
              type="checkbox"
              checked={adr}
              onChange={(e) => setAdr(e.target.checked)}
            />
            допуск ADR
          </label>

          <div className="divider" style={{ marginTop: 12 }} />
          <div className="meta" style={{ marginBottom: 4 }}>
            Габариты гружёной машины — необязательно, паспортные данные
            техники про запас (карта сейчас строит маршрут без них).
          </div>
          <div className="field-row">
            <div className="field">
              <label>Высота, м</label>
              <input
                type="number"
                inputMode="decimal"
                value={heightM}
                onChange={(e) => setHeightM(e.target.value)}
              />
            </div>
            <div className="field">
              <label>Ширина, м</label>
              <input
                type="number"
                inputMode="decimal"
                value={widthM}
                onChange={(e) => setWidthM(e.target.value)}
              />
            </div>
          </div>
          <div className="field-row">
            <div className="field">
              <label>Длина, м</label>
              <input
                type="number"
                inputMode="decimal"
                value={lengthM}
                onChange={(e) => setLengthM(e.target.value)}
              />
            </div>
            <div className="field">
              <label>Полная масса гружёной, т</label>
              <input
                type="number"
                inputMode="decimal"
                value={grossT}
                onChange={(e) => setGrossT(e.target.value)}
              />
            </div>
          </div>

          <div className="row" style={{ gap: 8, marginTop: 12 }}>
            <button disabled={busy} onClick={() => void save()}>
              {busy ? 'Сохраняю…' : 'Сохранить'}
            </button>
            <button
              className="secondary"
              disabled={busy}
              onClick={() => setEditing(false)}
            >
              Отмена
            </button>
          </div>
        </div>
      )}

      <h3 style={{ marginTop: 16 }}>История рейсов</h3>
      {loading && <div className="meta">Загружаю…</div>}
      {!loading && trips.length === 0 && (
        <div className="empty">Рейсов на этой технике ещё не было.</div>
      )}
      {trips.map((t) => (
        <div key={t.orderId} className="doc-row">
          <span>
            <strong>№ {t.number}</strong> · {t.route}
            <div className="meta">
              {ORDER_STATUS_LABEL[t.status]} · {t.driverName} ·{' '}
              {new Date(t.createdAt).toLocaleDateString('ru-RU')}
            </div>
          </span>
          <span style={{ textAlign: 'right' }}>
            <strong>{formatRub(t.payoutRub)}</strong>
            <div className="meta">расходы {formatRub(t.expensesRub)}</div>
          </span>
        </div>
      ))}

      <h3 style={{ marginTop: 16 }}>Документы</h3>
      {canManage && (
        <UploadButton
          kind="VEHICLE_DOC"
          vehicleId={vehicle.id}
          label="Добавить документ"
          camera
          onDone={(row) => {
            setFiles((prev) => [row, ...prev]);
            toast.success('Документ добавлен');
          }}
        />
      )}
      {!loading && files.length === 0 && (
        <div className="empty">Документов пока нет.</div>
      )}
      <FileGrid
        items={files}
        onDelete={(id) => setFiles((prev) => prev.filter((f) => f.id !== id))}
      />
    </>
  );
}
